
import React from 'react';

import Joi from 'joi';
import Form from '../../common/form';
import Button from '../../themed/button';
import Conditional from '../../common/conditional';

import { func, object } from 'prop-types';
import { getFileUploadTypes } from '../../../utils/utilities';
import { IMAGE_LIMITS, MATERIAL_OPTIONS } from '../../../utils/constants';

import toastService from '../../../services/toastService';

class TOFDScanPlan extends Form {
	state = {
		data: {
			wallThickness: 0,
            bevelAngle: 0,
            weldBevelShape: '',
            weldCondition: '',
            scanType: '',
            materialType: '',
            probeAngle: '',
            probeSerialNumber: '',
            calibrationBlockThickness: 0,
            scanSide: '',
            inspectionDate: new Date(),
            equipmentModal: '',
            equipmentSerialNumber: '',
            equipmentCalibrationDueDate: new Date(),
            referenceDB: 0,
            images: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
        id: Joi.number().optional(),
		wallThickness: Joi.number().positive().max(500).required().label('Wall Thickness (mm)'),
        bevelAngle: Joi.number().min(5).max(360).required().label('Bevel Angle (deg)'),
        weldBevelShape: Joi.string().required().label('Weld Bevel Shape'),
        weldCondition: Joi.string().required().label('Weld Condition'),
        scanType: Joi.string().required().label('Scan Type'),
        materialType: Joi.string().required().label('Material Type'),
        probeAngle: Joi.string().required().label('Probe Angle'),
        probeSerialNumber: Joi.string().required().label('Probe Serial Number'),
        calibrationBlockThickness: Joi.number().positive().required().label('Calibration Block Thickness (mm)'),
        scanSide: Joi.string().required().label('Scan Side'),
        equipmentModal: Joi.string().required().label('Equipment Modal'),
        equipmentSerialNumber: Joi.string().required().label('Equipment Serial Number'),
        equipmentCalibrationDueDate: Joi.date().greater('now').required().label('Equipment Calibration Due Date'),
        referenceDB: Joi.number().min(1).max(100).required().label('Reference dB'),
        inspectionDate: Joi.date().less('now').required().label('Inspection Date'),
        images: Joi.array().min(1).max(IMAGE_LIMITS.PAUT_SCAN_PLAN).required().label('Scan Plan Image')
	};

	static propTypes = {
        initialTOFDItem: object,
        onDeleteItem: object,
		onSave: func,
	};

    componentDidMount(): void {
        if (this.props.initialTOFDItem) {
            this.setState({ data: this.props.initialTOFDItem });
        }
    }

	doSubmit = (): void => {
		this.props.onSave(this.state.data);

        if (!this.props.initialTOFDItem) this.resetData();

        toastService.success('Changes saved!')
	};

    resetData = () => {
        this.setState({
            data: {
                wallThickness: 0,
                bevelAngle: 0,
                weldBevelShape: '',
                weldCondition: '',
                scanType: '',
                materialType: '',
                probeAngle: '',
                probeSerialNumber: '',
                calibrationBlockThickness: 0,
                scanSide: '',
                equipmentModal: '',
                equipmentSerialNumber: '',
                equipmentCalibrationDueDate: new Date(),
                referenceDB: 0,
                inspectionDate: new Date(),
                images: [],
            },
        });
    }

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<h3 className="form-title">Scan Plan Item</h3>

				<div className="container">
                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'wallThickness',
                                label: 'Wall Thickness (mm)',
                                placeholder: 'Enter Wall Thickness',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'bevelAngle',
                                label: 'Bevel Angle (deg)',
                                placeholder: 'Enter Bevel Angle',
                            })}
                        </div>

                        <div className="col">
                            {this.renderSelect({
                                name: 'weldBevelShape',
                                label: 'Weld Bevel Shape',
                                placeholder: 'Select Weld Bevel Shape',
                                options: WELD_BEVEL_SHAPE,
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                name: 'weldCondition',
                                label: 'Weld Condition',
                                placeholder: 'Select Weld Condition',
                                options: WELD_CONDITIONS,
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                name: 'scanType',
                                label: 'Scan Type',
                                placeholder: 'Select Scan Type',
                                options: SCAN_TYPES,
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderSelect({
                                name: 'materialType',
                                label: 'Material Type',
                                placeholder: 'Select Material Type',
                                options: MATERIAL_OPTIONS,
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'probeAngle',
                                label: 'Probe Angle (deg)',
                                placeholder: 'Enter Probe Angle e.g. 45, 70, 90',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'probeSerialNumber',
                                label: 'Probe Serial Number',
                                placeholder: 'Enter Probe Serial Number',
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'calibrationBlockThickness',
                                label: 'Calibration Block Thickness (mm)',
                                placeholder: 'Enter Calibration Block Thickness',
                            })}
                        </div>

                        <div className="col">
                            {this.renderSelect({
                                name: 'scanSide',
                                label: 'Scan Side',
                                placeholder: 'Select Scan Side',
                                options: SCAN_SIDES,
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'equipmentModal',
                                label: 'Equipment Modal',
                                placeholder: 'Enter Equipment Modal',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'equipmentSerialNumber',
                                label: 'Equipment Serial Number',
                                placeholder: 'Enter Equipment Serial Number',
                            })}
                        </div>

                        <div className="col">
                            {this.renderDatePicker({
                                name: 'equipmentCalibrationDueDate',
                                label: 'Equipment Calibration Due Date',
                                placeholderText: 'Select Equipment Calibration Due Date',
                                minDate: new Date(),
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'referenceDB',
                                label: 'Reference DB',
                                placeholder: 'Enter Reference DB',
                            })}
                        </div>
                    
                        <div className="col">
                            {this.renderDatePicker({
                                name: 'inspectionDate',
                                label: 'Inspection Date',
                                placeholderText: 'Select Inspection Date',
                                maxDate: new Date(),
                            })}
                        </div>  
                    </div>

                    <div className="divider my-4"></div>

                    {this.renderFileInput({
                        max: IMAGE_LIMITS.PAUT_SCAN_PLAN,
                        multiple: IMAGE_LIMITS.PAUT_SCAN_PLAN > 1,
                        name: 'images',
                        accept: 'image/*',
                        label: 'Images',
                        sublabel: 'Upload Images',
                        types: getFileUploadTypes(),
                    })}
                </div>

                <div className="divider my-4"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!this.props.onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={this.props.onDeleteItem} 
                                icon="trash"
                                label="Delete" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    {this.renderButton('Save')}
                </div>
			</form>
		);
	}
}

const WELD_BEVEL_SHAPE = [
    {
        id: 'Single V',
        name: 'Single V'
    },
    {
        id: 'Double V',
        name: 'Double V'
    },
];

const WELD_CONDITIONS = [
    {
        id: 'As Weld',
        name: 'As Weld'
    },
    {
        id: 'Surface Prepared',
        name: 'Surface Prepared'
    },
];

const SCAN_TYPES = [
    {
        id: 'Auto',
        name: 'Auto'
    },
    {
        id: 'Manual',
        name: 'Manual'
    },
];

const SCAN_SIDES = [
    {
        id: 'Internal',
        name: 'Internal'
    },
    {
        id: 'External',
        name: 'External'
    },
    {
        id: 'Internal & External',
        name: 'Internal & External'
    },
];

export default TOFDScanPlan;
